export type UsagePlanCreditRefreshState =
  | "trial_daily"
  | "shopify_pending_daily"
  | "monthly";

export interface UsagePlanCreditSummaryLike {
  provider?: string | null;
  billingProvider?: string | null;
  billing_provider?: string | null;
  paymentProvider?: string | null;
  payment_provider?: string | null;
  reasonCode?: string | null;
  reason_code?: string | null;
  paymentStatus?: string | null;
  payment_status?: string | null;
  shopifyPaymentStatus?: string | null;
  shopify_payment_status?: string | null;
  paymentPending?: boolean | null;
  payment_pending?: boolean | null;
  isPaymentPending?: boolean | null;
  is_payment_pending?: boolean | null;
  shopifyPaymentPending?: boolean | null;
  shopify_payment_pending?: boolean | null;
  planCreditRefreshCadence?: string | null;
  plan_credit_refresh_cadence?: string | null;
  planCreditsCadence?: string | null;
  plan_credits_cadence?: string | null;
  refreshCadence?: string | null;
  refresh_cadence?: string | null;
}

const normalizeToken = (value?: string | null) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const getFirstToken = (
  source: UsagePlanCreditSummaryLike | null | undefined,
  keys: (keyof UsagePlanCreditSummaryLike)[],
) => {
  for (const key of keys) {
    const token = normalizeToken(source?.[key] as string | null | undefined);
    if (token) return token;
  }
  return "";
};

const getFirstBoolean = (
  source: UsagePlanCreditSummaryLike | null | undefined,
  keys: (keyof UsagePlanCreditSummaryLike)[],
) => keys.some((key) => source?.[key] === true);

const hasPendingMeaning = (value: string) =>
  value.includes("pending") ||
  value.includes("awaiting") ||
  value.includes("unpaid") ||
  value.includes("not_received");

const hasDailyMeaning = (value: string) =>
  value === "daily" || value.includes("daily") || value.includes("day");

const hasShopifyMeaning = (value: string) => value.includes("shopify");

const readUsagePlanCreditSignals = ({
  creditSummary,
  subscriptionSummary,
  workspacePlan,
  billingProvider,
}: {
  creditSummary?: UsagePlanCreditSummaryLike | null;
  subscriptionSummary?: UsagePlanCreditSummaryLike | null;
  workspacePlan?: UsagePlanCreditSummaryLike | null;
  billingProvider?: string | null;
}) => {
  const signalSources = [creditSummary, subscriptionSummary, workspacePlan];
  const getTokenFromSignals = (keys: (keyof UsagePlanCreditSummaryLike)[]) => {
    for (const source of signalSources) {
      const token = getFirstToken(source, keys);
      if (token) return token;
    }
    return "";
  };
  const getBooleanFromSignals = (keys: (keyof UsagePlanCreditSummaryLike)[]) =>
    signalSources.some((source) => getFirstBoolean(source, keys));

  const provider =
    getTokenFromSignals([
      "provider",
      "billingProvider",
      "billing_provider",
      "paymentProvider",
      "payment_provider",
    ]) || normalizeToken(billingProvider);
  const reasonCode = getTokenFromSignals(["reasonCode", "reason_code"]);
  const paymentStatus = getTokenFromSignals([
    "shopifyPaymentStatus",
    "shopify_payment_status",
    "paymentStatus",
    "payment_status",
  ]);
  const refreshCadence = getTokenFromSignals([
    "planCreditRefreshCadence",
    "plan_credit_refresh_cadence",
    "planCreditsCadence",
    "plan_credits_cadence",
    "refreshCadence",
    "refresh_cadence",
  ]);
  const explicitShopifyPaymentPending = getBooleanFromSignals([
    "shopifyPaymentPending",
    "shopify_payment_pending",
  ]);
  const explicitPaymentPending =
    explicitShopifyPaymentPending ||
    getBooleanFromSignals([
      "paymentPending",
      "payment_pending",
      "isPaymentPending",
      "is_payment_pending",
    ]);
  const stateTokens = [provider, reasonCode, paymentStatus, refreshCadence];
  const isShopify = stateTokens.some(hasShopifyMeaning);
  const isPaymentPending =
    explicitPaymentPending ||
    hasPendingMeaning(reasonCode) ||
    hasPendingMeaning(paymentStatus) ||
    hasPendingMeaning(refreshCadence);
  const isDailyRefresh = hasDailyMeaning(refreshCadence);
  const isExplicitShopifyPendingPayment =
    explicitShopifyPaymentPending ||
    (hasShopifyMeaning(reasonCode) && hasPendingMeaning(reasonCode)) ||
    (hasShopifyMeaning(paymentStatus) && hasPendingMeaning(paymentStatus)) ||
    (hasShopifyMeaning(refreshCadence) && hasPendingMeaning(refreshCadence));
  const isExplicitShopifyPendingDaily =
    (hasShopifyMeaning(reasonCode) && hasPendingMeaning(reasonCode)) ||
    (hasShopifyMeaning(refreshCadence) &&
      hasPendingMeaning(refreshCadence) &&
      hasDailyMeaning(refreshCadence));

  return {
    isDailyRefresh,
    isExplicitShopifyPendingDaily,
    isExplicitShopifyPendingPayment,
    isPaymentPending,
    isShopify,
  };
};

export const resolveShopifyPendingPaymentNotice = ({
  isTrial,
  creditSummary,
  subscriptionSummary,
  workspacePlan,
  billingProvider,
}: {
  isTrial: boolean;
  creditSummary?: UsagePlanCreditSummaryLike | null;
  subscriptionSummary?: UsagePlanCreditSummaryLike | null;
  workspacePlan?: UsagePlanCreditSummaryLike | null;
  billingProvider?: string | null;
}) => {
  if (isTrial) return false;

  const signals = readUsagePlanCreditSignals({
    creditSummary,
    subscriptionSummary,
    workspacePlan,
    billingProvider,
  });

  return (
    signals.isExplicitShopifyPendingPayment ||
    (signals.isShopify && signals.isPaymentPending)
  );
};

export const resolveUsagePlanCreditRefreshState = ({
  isTrial,
  creditSummary,
  subscriptionSummary,
  workspacePlan,
  billingProvider,
}: {
  isTrial: boolean;
  creditSummary?: UsagePlanCreditSummaryLike | null;
  subscriptionSummary?: UsagePlanCreditSummaryLike | null;
  workspacePlan?: UsagePlanCreditSummaryLike | null;
  billingProvider?: string | null;
}): UsagePlanCreditRefreshState => {
  if (isTrial) return "trial_daily";

  const signals = readUsagePlanCreditSignals({
    creditSummary,
    subscriptionSummary,
    workspacePlan,
    billingProvider,
  });

  if (
    signals.isExplicitShopifyPendingDaily ||
    (signals.isShopify && signals.isPaymentPending && signals.isDailyRefresh)
  ) {
    return "shopify_pending_daily";
  }

  return "monthly";
};
